import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const categories = [
  { value: "news", label: "News" },
  { value: "logistics", label: "Logistics" },
  { value: "market-insights", label: "Market Insights" },
  { value: "company", label: "Company" },
  { value: "brand-spotlight", label: "Brand Spotlight" },
];

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  category: string | null;
  image_url: string | null;
  published_at: string | null;
  is_published: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function NewsAdminPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formExcerpt, setFormExcerpt] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formCategory, setFormCategory] = useState("news");
  const [formImageUrl, setFormImageUrl] = useState("");
  const [formPublished, setFormPublished] = useState(true);

  const resetForm = useCallback(() => {
    setFormTitle("");
    setFormSlug("");
    setFormExcerpt("");
    setFormContent("");
    setFormCategory("news");
    setFormImageUrl("");
    setFormPublished(true);
    setEditingPost(null);
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("news-admin", {
        body: { action: "list" },
      });

      if (fnErr) throw fnErr;
      if (!data?.success) throw new Error(data?.message || "Failed to load posts");
      setPosts((data.data as Post[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleEdit = (post: Post) => {
    setEditingPost(post);
    setFormTitle(post.title);
    setFormSlug(post.slug);
    setFormExcerpt(post.excerpt || "");
    setFormContent(post.content || "");
    setFormCategory(post.category || "news");
    setFormImageUrl(post.image_url || "");
    setFormPublished(post.is_published ?? true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("news-admin", {
        body: { action: "delete", data: { id } },
      });

      if (fnErr) throw fnErr;
      if (!data?.success) throw new Error(data?.message || "Failed to delete");
      await loadPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      setError("Title is required");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: formTitle.trim(),
        slug: formSlug.trim() || undefined,
        excerpt: formExcerpt.trim() || undefined,
        content: formContent.trim() || undefined,
        category: formCategory,
        image_url: formImageUrl.trim() || undefined,
        is_published: formPublished,
      };

      if (editingPost) {
        const { data, error: fnErr } = await supabase.functions.invoke("news-admin", {
          body: { action: "update", data: { id: editingPost.id, ...payload } },
        });
        if (fnErr) throw fnErr;
        if (!data?.success) throw new Error(data?.message || "Failed to update");
      } else {
        const { data, error: fnErr } = await supabase.functions.invoke("news-admin", {
          body: { action: "create", data: payload },
        });
        if (fnErr) throw fnErr;
        if (!data?.success) throw new Error(data?.message || "Failed to create");
      }

      resetForm();
      setShowForm(false);
      await loadPosts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB");
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const result = reader.result as string;
          const base64String = result.split(",")[1];
          if (base64String) resolve(base64String);
          else reject(new Error("Failed to read file"));
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
      });

      const { data, error: fnErr } = await supabase.functions.invoke("news-admin", {
        body: {
          action: "upload",
          data: {
            filename: file.name,
            contentType: file.type,
            base64,
          },
        },
      });

      if (fnErr) throw fnErr;
      if (!data?.success) throw new Error(data?.message || "Upload failed");

      setFormImageUrl(data.data?.url || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const generateSlug = () => {
    const slug = formTitle
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setFormSlug(slug);
  };

  return (
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-100 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Admin
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl leading-[1.05] font-light">
              News Management
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/news"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-background-200/70 text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-all font-label text-[12px] tracking-[0.12em] uppercase"
            >
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
              View News Page
            </Link>
            <button
              onClick={() => {
                resetForm();
                setShowForm((v) => !v);
              }}
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[12px] tracking-[0.12em] uppercase"
            >
              <i className={`${showForm ? "ri-close-line" : "ri-add-line"} w-4 h-4 flex items-center justify-center`} />
              {showForm ? "Cancel" : "New Post"}
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[14px] flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="mb-10 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
          >
            <h2 className="font-heading text-foreground-950 text-xl md:text-2xl mb-6">
              {editingPost ? "Edit Post" : "Create New Post"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Title */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-[15px] focus:border-accent-400 focus:outline-none transition-colors"
                  placeholder="Enter post title"
                  required
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Slug
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formSlug}
                    onChange={(e) => setFormSlug(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-[15px] focus:border-accent-400 focus:outline-none transition-colors"
                    placeholder="auto-generated-from-title"
                  />
                  <button
                    type="button"
                    onClick={generateSlug}
                    className="whitespace-nowrap cursor-pointer px-4 py-3 rounded-xl bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors font-label text-[12px] tracking-[0.06em]"
                  >
                    Generate
                  </button>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Category
                </label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-[15px] focus:border-accent-400 focus:outline-none transition-colors cursor-pointer"
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Excerpt */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Excerpt
                </label>
                <textarea
                  value={formExcerpt}
                  onChange={(e) => setFormExcerpt(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-[15px] focus:border-accent-400 focus:outline-none transition-colors resize-none"
                  placeholder="Short summary for the news listing (max 500 chars)"
                />
                <p className="mt-1 text-[11px] text-foreground-400 text-right">
                  {formExcerpt.length}/500
                </p>
              </div>

              {/* Content */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Content
                </label>
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  rows={8}
                  className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-[15px] focus:border-accent-400 focus:outline-none transition-colors resize-none"
                  placeholder="Full article content. Use blank lines to separate paragraphs."
                />
              </div>

              {/* Image Upload */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-label tracking-[0.06em] text-foreground-700 mb-2">
                  Featured Image
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors font-label text-[12px] tracking-[0.06em] disabled:opacity-50"
                  >
                    {uploadingImage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-secondary-400 border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ri-image-add-line w-4 h-4 flex items-center justify-center" />
                        Upload Image
                      </>
                    )}
                  </button>
                  {formImageUrl && (
                    <button
                      type="button"
                      onClick={() => setFormImageUrl("")}
                      className="text-[12px] text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      Remove image
                    </button>
                  )}
                </div>
                {formImageUrl && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-background-200/70 w-full max-w-md h-40">
                    <img
                      src={formImageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              {/* Published toggle */}
              <div className="md:col-span-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormPublished((v) => !v)}
                  className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                    formPublished ? "bg-accent-500" : "bg-background-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50 transition-transform ${
                      formPublished ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-[14px] font-label text-foreground-700">
                  {formPublished ? "Published" : "Draft"}
                </span>
              </div>
            </div>

            {/* Form actions */}
            <div className="mt-8 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[13px] tracking-[0.12em] uppercase disabled:opacity-50"
              >
                {saving && (
                  <div className="w-4 h-4 border-2 border-background-200 border-t-background-50 rounded-full animate-spin" />
                )}
                {editingPost ? "Save Changes" : "Publish Post"}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                className="whitespace-nowrap cursor-pointer px-6 py-3 rounded-full border border-background-200/70 text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-all font-label text-[13px] tracking-[0.12em] uppercase"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Posts list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-2 border-accent-200 border-t-accent-500 rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200/70">
            <i className="ri-article-line text-4xl text-foreground-300 mb-4 block" />
            <p className="text-foreground-500 text-[15px]">No posts yet. Click "New Post" to get started.</p>
          </div>
        ) : (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="px-5 py-4 text-[11px] font-label tracking-[0.12em] uppercase text-foreground-500">
                      Post
                    </th>
                    <th className="px-5 py-4 text-[11px] font-label tracking-[0.12em] uppercase text-foreground-500">
                      Category
                    </th>
                    <th className="px-5 py-4 text-[11px] font-label tracking-[0.12em] uppercase text-foreground-500">
                      Date
                    </th>
                    <th className="px-5 py-4 text-[11px] font-label tracking-[0.12em] uppercase text-foreground-500">
                      Status
                    </th>
                    <th className="px-5 py-4 text-[11px] font-label tracking-[0.12em] uppercase text-foreground-500 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr
                      key={post.id}
                      className="border-b border-background-100 last:border-b-0 hover:bg-background-100/50 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {post.image_url ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-background-100">
                              <img
                                src={post.image_url}
                                alt={post.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                              <i className="ri-article-line text-secondary-500" />
                            </div>
                          )}
                          <div>
                            <p className="font-heading text-foreground-950 text-[15px] leading-tight">
                              {post.title}
                            </p>
                            <p className="text-[12px] text-foreground-400 mt-0.5">/{post.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-label tracking-[0.06em] uppercase">
                          {categories.find((c) => c.value === post.category)?.label || post.category || "News"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-[13px] text-foreground-600">
                        {formatDate(post.published_at || "")}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-label tracking-[0.06em] uppercase ${
                            post.is_published
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {post.is_published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/news/${post.slug}`}
                            target="_blank"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer"
                            title="View"
                          >
                            <i className="ri-eye-line" />
                          </Link>
                          <button
                            onClick={() => handleEdit(post)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-700 hover:bg-accent-50 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-pencil-line" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}